/**
 * ContabilidadDialogs.js — extracted Payment + Profit + Delete dialogs.
 * Drops ContabilidadPage.js from 823 → ~570 lines by isolating ~250 lines
 * of form JSX. Pure presentation, state lives in the parent.
 */
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Receipt, PiggyBank, CalendarIcon, Loader2 } from 'lucide-react';

const formatCurrency = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PaymentDialog = ({
  open,
  onClose,
  onSubmit,
  editingPayment,
  paymentForm,
  setPaymentForm,
  sortedGestores,
  pricingGuides,
  submitting,
}) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-blue-600" />
          {editingPayment ? 'Editar Pago' : 'Registrar Pago de Gestor'}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Gestor *</Label>
          <Select
            value={paymentForm.gestor_id}
            onValueChange={(value) => setPaymentForm({ ...paymentForm, gestor_id: value })}
          >
            <SelectTrigger className="bg-slate-50">
              <SelectValue placeholder="Selecciona un gestor" />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={4} className="z-[100]">
              {sortedGestores.map((g, index) => (
                <SelectItem key={g.id} value={g.id}>
                  {index < 3 ? `🏆 ` : ''}{g.name} - Vendido: {formatCurrency(g.total_sales || 0)} | Pend: {formatCurrency(g.pending_payment)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Monto Recibido (USD) *</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={paymentForm.amount}
            onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
            placeholder="500"
            required
            className="bg-slate-50"
          />
        </div>

        <div className="space-y-2">
          <Label>Fecha *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start bg-slate-50">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(paymentForm.payment_date, 'dd/MM/yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={paymentForm.payment_date}
                onSelect={(date) => date && setPaymentForm({ ...paymentForm, payment_date: date })}
                locale={es}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>Descripción (opcional)</Label>
          <Input
            value={paymentForm.description}
            onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
            placeholder="Pago quincenal, abono, etc."
            className="bg-slate-50"
          />
        </div>

        <div className="space-y-2 p-3 bg-amber-50/50 rounded-lg border border-amber-100">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="has_commission"
              checked={paymentForm.has_commission}
              onChange={(e) => {
                const checked = e.target.checked;
                const defaultPct = pricingGuides.length > 0 ? pricingGuides[0].commission_pct : '';
                const amt = parseFloat(paymentForm.amount) || 0;
                const commAmt = checked && defaultPct ? (amt * defaultPct / 100).toFixed(2) : '';
                setPaymentForm({ ...paymentForm, has_commission: checked, commission_pct: checked ? defaultPct.toString() : '', commission: commAmt });
              }}
              className="w-4 h-4 rounded border-slate-300"
              data-testid="payment-commission-check"
            />
            <Label htmlFor="has_commission" className="cursor-pointer text-sm">Este pago tuvo comision por recibirlo</Label>
          </div>
          {paymentForm.has_commission && (
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-3">
                <div className="space-y-1 flex-1">
                  <Label className="text-xs">Guia de precio</Label>
                  <select
                    value={paymentForm.commission_pct}
                    onChange={(e) => {
                      const pct = parseFloat(e.target.value) || 0;
                      const amt = parseFloat(paymentForm.amount) || 0;
                      setPaymentForm({ ...paymentForm, commission_pct: e.target.value, commission: (amt * pct / 100).toFixed(2) });
                    }}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                    data-testid="payment-commission-guide"
                  >
                    <option value="">Seleccionar guia...</option>
                    {pricingGuides.map(g => (
                      <option key={g.id} value={g.commission_pct}>{g.service_name} — {g.commission_pct}%</option>
                    ))}
                    <option value="custom">Personalizado</option>
                  </select>
                </div>
                <div className="space-y-1 w-24">
                  <Label className="text-xs">% Comision</Label>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    value={paymentForm.commission_pct === 'custom' ? '' : paymentForm.commission_pct}
                    onChange={(e) => {
                      const pct = parseFloat(e.target.value) || 0;
                      const amt = parseFloat(paymentForm.amount) || 0;
                      setPaymentForm({ ...paymentForm, commission_pct: e.target.value, commission: (amt * pct / 100).toFixed(2) });
                    }}
                    className="bg-white"
                    data-testid="payment-commission-pct"
                  />
                </div>
                <div className="text-right pt-4">
                  <p className="text-xs text-slate-500">Comision</p>
                  <p className="font-bold text-amber-700">${paymentForm.commission || '0.00'}</p>
                </div>
              </div>
              {parseFloat(paymentForm.amount) > 0 && parseFloat(paymentForm.commission) > 0 && (
                <div className="flex justify-between text-sm p-2 bg-white rounded border border-green-200">
                  <span className="text-slate-500">Neto (Pago - Comision):</span>
                  <span className="font-bold text-green-700">${(parseFloat(paymentForm.amount) - parseFloat(paymentForm.commission)).toFixed(2)}</span>
                </div>
              )}
              <p className="text-xs text-slate-400">Se resta de la ganancia. No afecta el saldo del gestor.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={submitting || !paymentForm.gestor_id}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingPayment ? 'Guardar Cambios' : 'Registrar Pago')}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const ProfitDialog = ({
  open,
  onClose,
  onSubmit,
  editingProfit,
  profitForm,
  setProfitForm,
  submitting,
}) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-600" />
          {editingProfit ? 'Editar Ganancia' : 'Registrar Ganancia'}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Monto (USD) *</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={profitForm.amount}
            onChange={(e) => setProfitForm({ ...profitForm, amount: e.target.value })}
            placeholder="1000"
            required
            className="bg-slate-50"
          />
        </div>

        <div className="space-y-2">
          <Label>Fecha *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start bg-slate-50">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(profitForm.entry_date, 'dd/MM/yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={profitForm.entry_date}
                onSelect={(date) => date && setProfitForm({ ...profitForm, entry_date: date })}
                locale={es}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>Descripción (opcional)</Label>
          <Input
            value={profitForm.description}
            onChange={(e) => setProfitForm({ ...profitForm, description: e.target.value })}
            placeholder="Comisión, ganancia por venta, etc."
            className="bg-slate-50"
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-green-600 hover:bg-green-700"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingProfit ? 'Guardar Cambios' : 'Registrar Ganancia')}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const ContabilidadDeleteDialog = ({ open, onClose, onConfirm, targetName }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Confirmar eliminación</DialogTitle>
      </DialogHeader>
      <p className="text-slate-600">
        ¿Eliminar este registro de <strong>{targetName}</strong>?
      </p>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="destructive" onClick={onConfirm}>
          Eliminar
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
